import {
  FilterOperators,
  IColumnFilterMDS,
  IColumnGroupColumn,
  IProjection,
  IReportDatasetLens,
  ISingleValueColumnFilter
} from '@opengov/reporting-types';
import { allFilterOptions } from '../filterOptions';
import { isArray } from 'lodash';

type OperatorsObject = Partial<Record<FilterOperators, string>>;

const OPERATORS: Record<string, string> = {
  [FilterOperators.Equal]: '=',
  [FilterOperators.NotEqual]: '≠',
  [FilterOperators.Greater]: '≥',
  [FilterOperators.Lesser]: '≤'
};

function getOperatorsLabel(filterOptions: typeof allFilterOptions) {
  //this function returns an object that defines a convention in key/value format
  //for the corresponding operator.id/operator.label required for the filters tooltip
  const operators: OperatorsObject = filterOptions.reduce((operatorsObject: OperatorsObject, filterOption) => {
    const operatorLabel = OPERATORS[filterOption.id] ?? filterOption.label;
    return { ...operatorsObject, [filterOption.id]: operatorLabel };
  }, {});
  return operators;
}

function getFilterColumnLabel(filterColumnName: string, lens: IReportDatasetLens) {
  const columnLabel = lens.columns.find((column) => column.name == filterColumnName)?.label;
  const COAcolumnLabel =
    lens.dimensions
      ?.map((dimension) => dimension.columns.find((column) => column.name === filterColumnName)?.label)
      .find((label) => label !== undefined) || '';
  return COAcolumnLabel || columnLabel || filterColumnName;
}

function isSingleValueColumnFilter(
  filter: ISingleValueColumnFilter<IProjection> | string | number
): filter is ISingleValueColumnFilter<IProjection> {
  return typeof filter === 'object' && 'columnName' in filter && 'value' in filter && 'operator' in filter;
}

export function getColumnFiltersText(
  columnFilters: IColumnFilterMDS<IProjection>[] | undefined,
  lens: IReportDatasetLens
) {
  const operators = getOperatorsLabel(allFilterOptions);

  if (!columnFilters || columnFilters.length === 0) {
    return [];
  }

  return columnFilters.reduce<string[]>((acc, columnFilterList) => {
    const { value: columnFilterListItem } = columnFilterList;
    if (isArray(columnFilterListItem)) {
      columnFilterListItem.forEach((filter) => {
        if (isSingleValueColumnFilter(filter)) {
          const columnLabel = getFilterColumnLabel(filter.columnName, lens);
          const useQuotes = filter.value && !Number(filter.value);
          const filterValue = useQuotes ? `'${filter.value as string}'` : (filter.value as string);
          const rawOperator = operators[filter.operator as FilterOperators] || '';
          const operatorLabel = rawOperator.toLowerCase();
          acc.push(`${columnLabel} ${operatorLabel} ${filterValue}`);
        }
      });
    }
    return acc;
  }, []);
}

export function countFilters(
  dateFilter: ISingleValueColumnFilter<IColumnGroupColumn>[] = [],
  filters: IColumnFilterMDS<IProjection>[] = []
) {
  return filters.length + (dateFilter.length > 0 ? 1 : 0);
}
